import { Suspense } from 'react'
import { Link } from 'react-router-dom'
import { ShieldCheck, Zap, Star, Droplets, Clock, BadgePoundSterling, Wrench, Flame } from 'lucide-react'
import { useDirectoryMode } from '../../hooks/useDirectoryMode'
import { DirectoryModeLogo } from '../../pages/directory/DirectoryModeLogo'
import { DirectoryPopularServices } from './DirectoryPopularServices'
import { ErrorBoundary } from '../ErrorBoundary'
import { GOOGLE_MAPS_API_KEY } from '../../lib/googleMaps'
import { lazyWithReload } from '../../lib/lazyWithReload'
import type { PlumberPin } from '../map/PlumberMap'

// Load the map code only when a sidebar with pins actually renders.
// lazyWithReload picks up the fresh chunk if a deploy replaced it under an open tab.
const PlumberMap = lazyWithReload(() => import('../map/PlumberMap').then(m => ({ default: m.PlumberMap })))

interface DirectorySidebarProps {
  count?: number
  areaLabel?: string
  emergencyCount?: number
  plumberPins?: PlumberPin[]
  /** Sibling-area links (nearby towns / postcodes) rendered as a sidebar card. */
  nearby?: { title: string; links: { label: string; href: string }[] }
}

export function DirectorySidebar({
  count,
  areaLabel,
  emergencyCount,
  plumberPins,
  nearby,
}: DirectorySidebarProps) {
  const { isEmergency, isGasSafe, isWaterSafe } = useDirectoryMode()
  const place = areaLabel ? ` in ${areaLabel}` : ' in your area'

  const ctaTitle = isEmergency
    ? `Need an emergency plumber${place} right now?`
    : isGasSafe
      ? `Need a Gas Safe plumber${place} right now?`
      : isWaterSafe
        ? `Need a WaterSafe plumber${place} right now?`
        : `Need a plumber${place} right now?`

  const ctaBody = isEmergency
    ? "We'll match you with an available emergency plumber in minutes - 24/7, including nights and weekends."
    : isGasSafe
      ? "We'll match you with a Gas Safe registered plumber quickly, including urgent boiler and gas-work callouts."
      : isWaterSafe
        ? "We'll match you with a WaterSafe approved plumber quickly for certified water fittings and plumbing work."
        : "We'll match you with an available plumber in minutes - fast local response when you need it."

  // Context-aware "About" panel
  const aboutHeading = isEmergency
    ? 'Emergency Plumbers'
    : isGasSafe
      ? 'Gas Safe Plumbers'
      : isWaterSafe
        ? 'WaterSafe Plumbers'
        : 'About FixDelivered'

  type Badge = { icon: React.ElementType; label: string; colour: string }

  const badges: Badge[] = isEmergency ? [
    { icon: Clock,              label: 'Available 24/7',         colour: 'bg-emergency-50 text-emergency-700 border-emergency-100' },
    { icon: Zap,                label: 'Dispatched in minutes',  colour: 'bg-amber-50 text-amber-700 border-amber-100' },
    { icon: ShieldCheck,        label: 'Min 4★ rated',           colour: 'bg-trust-50 text-trust-700 border-trust-100' },
    { icon: BadgePoundSterling, label: 'Max £350 callout fee',   colour: 'bg-gray-50 text-gray-600 border-gray-100' },
  ] : isGasSafe ? [
    { icon: ShieldCheck, label: 'Gas Safe registered',    colour: 'bg-blue-50 text-blue-700 border-blue-100' },
    { icon: Flame,       label: 'Legally qualified',      colour: 'bg-orange-50 text-orange-700 border-orange-100' },
    { icon: Wrench,      label: 'Boiler & gas work',      colour: 'bg-gray-50 text-gray-600 border-gray-100' },
    { icon: ShieldCheck, label: 'Checked profiles',       colour: 'bg-trust-50 text-trust-700 border-trust-100' },
  ] : isWaterSafe ? [
    { icon: Droplets,    label: 'WaterSafe approved',         colour: 'bg-cyan-50 text-cyan-700 border-cyan-100' },
    { icon: ShieldCheck, label: 'Water Regs compliant',       colour: 'bg-blue-50 text-blue-700 border-blue-100' },
    { icon: Wrench,      label: 'Certified fittings work',    colour: 'bg-gray-50 text-gray-600 border-gray-100' },
    { icon: ShieldCheck, label: 'Checked profiles',           colour: 'bg-trust-50 text-trust-700 border-trust-100' },
  ] : [
    { icon: ShieldCheck, label: 'Checked profiles',        colour: 'bg-trust-50 text-trust-700 border-trust-100' },
    { icon: Zap,         label: '24/7 Emergency Plumbing', colour: 'bg-emergency-50 text-emergency-700 border-emergency-100' },
    { icon: Star,        label: 'Real reviews',            colour: 'bg-amber-50 text-amber-700 border-amber-100' },
  ]

  return (
    <div className="space-y-4">

      {/* ── Gas Safe / WaterSafe mode logo ── */}
      {(isGasSafe || isWaterSafe) && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 sm:p-5 flex items-center justify-center">
          <DirectoryModeLogo />
        </div>
      )}

      {/* ── Emergency CTA ── */}
      <div className="bg-[#0f172a] rounded-2xl p-5 text-white">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-7 h-7 rounded-lg bg-emergency-500/20 flex items-center justify-center flex-shrink-0">
            <Zap className="h-3.5 w-3.5 text-emergency-400" />
          </div>
          <p className="text-sm font-semibold text-white">{ctaTitle}</p>
        </div>
        <p className="text-xs text-gray-400 leading-relaxed mb-4">{ctaBody}</p>
        <Link
          to="/"
          className="block bg-emergency-500 hover:bg-emergency-600 text-white font-bold text-sm text-center py-2.5 rounded-xl transition-colors"
        >
          Get emergency help &rarr;
        </Link>
      </div>

      {/* ── Map ── Only on pages that actually provide pins (county/town/postcode).
          Reserve the ~500px card while the pins load so 'About'/'Popular services'
          below don't jump when the map appears. Country/hub pages pass no pins
          (plumberPins === undefined), so they get no map and no empty placeholder.
          Neither does a build without a Google Maps key (see lib/googleMaps.ts), and if
          the map chunk still fails to load the boundary drops just the map card. */}
      {GOOGLE_MAPS_API_KEY && plumberPins !== undefined && count !== 0 && (
        plumberPins.length > 0 ? (
          <ErrorBoundary name="PlumberMap">
            <Suspense fallback={<div className="rounded-2xl bg-gray-100 animate-pulse" style={{ height: 500 }} />}>
              <PlumberMap pins={plumberPins} areaLabel={areaLabel} />
            </Suspense>
          </ErrorBoundary>
        ) : (
          <div className="rounded-2xl bg-gray-100 animate-pulse" style={{ height: 500 }} />
        )
      )}

      {/* ── Context-aware about panel ── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">{aboutHeading}</p>
        <div className="flex flex-wrap gap-2">
          {badges.map(({ icon: Icon, label, colour }) => (
            <span
              key={label}
              className={`inline-flex items-center gap-1 text-xs font-medium border px-2.5 py-1 rounded-full ${colour}`}
            >
              <Icon className="h-3 w-3" /> {label}
            </span>
          ))}
          {count !== undefined && count > 0 && (
            <span className="inline-flex items-center gap-1 text-xs font-medium bg-gray-50 text-gray-600 border border-gray-100 px-2.5 py-1 rounded-full">
              {count} Plumbers Listed{emergencyCount ? ` · ${emergencyCount} 24/7` : ''}
            </span>
          )}
        </div>
      </div>

      {/* ── Popular services ── */}
      <DirectoryPopularServices />

      {/* ── Nearby areas ── */}
      {nearby && nearby.links.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">{nearby.title}</p>
          <div className="flex flex-wrap gap-2">
            {nearby.links.map(l => (
              <Link
                key={l.href}
                to={l.href}
                className="px-2.5 py-1 rounded-full bg-gray-50 border border-gray-200 text-xs text-gray-700 hover:border-gray-300 transition-colors"
              >
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      )}

    </div>
  )
}
